import { useState, useRef, useEffect } from "react";
import { FaEdit, FaSms, FaSpinner, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { verifyOtp, requestOtp, reset } from "../features/auth/authSlice";
import { api } from "../utils/apiUtils";
import { getFcmToken } from "../firebase/getFcmToken";

export default function VerifyOtp() {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(120);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  
  const inputs = useRef([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const phone = sessionStorage.getItem("loginPhone") || "";
  const accountType = sessionStorage.getItem("loginAccountType") || "user";
  
  const { user, isSuccess } = useSelector((state) => state.auth);

  // Redirect if already logged in
  useEffect(() => {
    if (isSuccess && user) {
      dispatch(reset());
      navigate("/dashboard");
    }
  }, [isSuccess, user, navigate, dispatch]);

  // Format time remaining for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle OTP input changes and auto-focus
  const handleChange = (value, index) => {
    // Only allow digits
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus to next input
    if (value && index < 3) {
      inputs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Timer countdown effect
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");

    // Validation: Check if OTP is provided
    if (!otpString) {
      toast.error("Please enter the OTP");
      return;
    }

    // Validation: Check if OTP is valid 4-digit number
    if (!/^[0-9]{4}$/.test(otpString)) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }

    setIsVerifying(true);

    try {
      // Handle corporate staff OTP verification with unified API
      if (accountType === "corporate_staff") {
        const res = await api.post("/auth/verify-otp", {
          phone,
          otp: otpString,
          role: "staff",
        });

        if (res?.data?.success) {
          // Store both tokens
          const corporateToken = res?.data?.corporateToken || res?.data?.token;
          if (corporateToken) localStorage.setItem("corporateToken", corporateToken);

          const regularToken = res?.data?.token;
          if (regularToken && regularToken !== corporateToken) {
            localStorage.setItem("token", regularToken);
          }

          // Store corporate member data from 'data' field (consistent with Login.jsx)
          const member = res?.data?.data || {};
          localStorage.setItem("corporateMember", JSON.stringify(member));

          // IMPORTANT: Clear personal user session for corporate staff
          localStorage.removeItem("user");

          sessionStorage.removeItem("corporateBookingMode");
          window.dispatchEvent(new Event("corporateSessionChanged"));

          // Clear session storage
          sessionStorage.removeItem("loginPhone");
          sessionStorage.removeItem("loginAccountType");

          // Redirect to corporate profile page based on role
          const destination = member?.role === "admin" ? "/corporate/dashboard" : "/corporate/profile";
          navigate(destination, { replace: true });
        } else {
          // Handle specific error codes
          const errorCode = res?.data?.code;
          const errorMessage = res?.data?.message || "Invalid OTP";

          if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
            toast.error(`${errorMessage}. Please contact support for assistance.`);
          } else {
            toast.error(errorMessage);
          }
        }
      } else {
        // Handle personal user OTP verification
        let fcmToken = null;
        try {
          fcmToken = await getFcmToken();
        } catch (tokenErr) {
          console.warn("Failed to get FCM token for OTP login:", tokenErr);
        }

        const result = await dispatch(
          verifyOtp({ phone, otp: otpString, fcmToken })
        );

        if (verifyOtp.fulfilled.match(result)) {
          // Clear session storage
          sessionStorage.removeItem("loginPhone");
          sessionStorage.removeItem("loginAccountType");
          // Redux will handle redirect via useEffect above
        } else {
          toast.error(result.payload || "Invalid OTP");
        }
      }
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Network error. Please try again.";

      if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
        toast.error(`${errorMessage}. Please contact support for assistance.`);
      } else {
        toast.error(errorMessage);
      }
      console.error("OTP verification error:", error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);

    try {
      // Handle corporate staff OTP resend with unified API
      if (accountType === "corporate_staff") {
        const res = await api.post("/auth/request-otp", {
          phone,
          role: "staff",
        });

        if (res?.data?.success) {
          setTimeLeft(120);
          setCanResend(false);
          setOtp(["", "", "", ""]);
          inputs.current[0]?.focus();
        } else {
          // Handle specific error codes
          const errorCode = res?.data?.code;
          const errorMessage = res?.data?.message || "Failed to resend OTP";

          if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
            toast.error(`${errorMessage}. Please contact support for assistance.`);
          } else {
            toast.error(errorMessage);
          }
        }
      } else {
        // Handle personal user OTP resend
        const result = await dispatch(requestOtp({ phone }));

        if (requestOtp.fulfilled.match(result)) {
          setTimeLeft(120);
          setCanResend(false);
          setOtp(["", "", "", ""]);
          inputs.current[0]?.focus();
        } else {
          toast.error(result.payload || "Failed to resend OTP");
        }
      }
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Network error. Please try again.";

      if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
        toast.error(`${errorMessage}. Please contact support for assistance.`);
      } else {
        toast.error(errorMessage);
      }
      console.error("Resend OTP error:", error);
    } finally {
      setResendLoading(false);
    }
  };

  const handleEditPhone = () => {
    sessionStorage.removeItem("loginPhone");
    sessionStorage.removeItem("loginAccountType");
    navigate("/login");
  };

  const handleResendMethod = (method) => {
    if (!canResend) {
      toast.warning("Please wait for the timer to expire before resending");
      return;
    }

    console.log(`Resending OTP via ${method} to ${phone}`);
    handleResendOtp();
  };

  // Session validation
  if (!phone) {
    return (
      <div className="otp-container min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="otp-card w-full max-w-96 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-center mb-4">Session Expired</h2>
          <p className="text-center text-gray-600 mb-6">
            Your verification session has expired. Please start over.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="otp-container min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="otp-card w-full max-w-96 bg-white rounded-xl shadow-lg p-6">
        {/* Title */}
        <h2 className="otp-title text-3xl font-bold mb-2">Verify OTP</h2>

        {/* Subtitle with Edit Button */}
        <p className="otp-subtitle text-gray-600 flex items-center gap-2 mb-6">
          Enter 4 digit OTP sent
          <span className="flex items-center gap-1">
            <svg className="flag-icon w-5 h-5" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
              <rect width="900" height="200" fill="#FF9933"/>
              <rect y="200" width="900" height="200" fill="#FFFFFF"/>
              <rect y="400" width="900" height="200" fill="#138808"/>
              <circle cx="450" cy="300" r="60" fill="#1F41B6"/>
              <circle cx="450" cy="300" r="55" fill="none" stroke="#1F41B6" strokeWidth="2"/>
              <line x1="450" y1="245" x2="450" y2="355" stroke="#1F41B6" strokeWidth="1"/>
              <line x1="395" y1="300" x2="505" y2="300" stroke="#1F41B6" strokeWidth="1"/>
              <line x1="405" y1="255" x2="495" y2="345" stroke="#1F41B6" strokeWidth="1"/>
              <line x1="405" y1="345" x2="495" y2="255" stroke="#1F41B6" strokeWidth="1"/>
              <line x1="415" y1="270" x2="485" y2="330" stroke="#1F41B6" strokeWidth="1"/>
              <line x1="415" y1="330" x2="485" y2="270" stroke="#1F41B6" strokeWidth="1"/>
            </svg>
            +91 {phone}
          </span>
          <button
            type="button"
            onClick={handleEditPhone}
            className="edit-btn text-blue-600 cursor-pointer hover:text-blue-700 transition"
            aria-label="Edit phone number"
          >
            <FaEdit className="text-lg" />
          </button>
        </p>

        {/* OTP Input Fields */}
        <div className="otp-inputs flex justify-between mb-6 gap-2">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputs.current[index] = el)}
              type="text"
              value={digit}
              maxLength="1"
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className="otp-input w-16 h-16 border-2 border-blue-500 rounded-lg text-center text-2xl font-semibold outline-none focus:ring-2 focus:ring-blue-300 transition"
              inputMode="numeric"
              autoComplete="off"
              disabled={isVerifying || resendLoading}
            />
          ))}
        </div>

        {/* Resend Timer */}
        <p className="resend-timer text-gray-700 mb-3">
          Didn't get OTP? Resend OTP in <span className="font-bold">{formatTime(timeLeft)}</span>
        </p>

        {/* Resend Method Buttons */}
        <div className="resend-buttons mb-6">
          <button
            type="button"
            onClick={() => handleResendMethod("sms")}
            className="sms-btn w-full border border-gray-300 rounded-lg py-2 px-3 flex items-center justify-center gap-2 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canResend || resendLoading || isVerifying}
          >
            {resendLoading ? (
              <FaSpinner className="text-gray-500 animate-spin" />
            ) : (
              <FaSms className="text-gray-500 text-lg" />
            )}
            <span className="text-sm font-medium">Resend</span>
          </button>
        </div>

        {/* Submit Button */}
        <form onSubmit={handleSubmit}>
          <button
            type="submit"
            disabled={isVerifying || resendLoading}
            className={`submit-btn w-full py-3 rounded-lg font-semibold mb-4 transition flex items-center justify-center gap-2 ${
              isVerifying || resendLoading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {isVerifying || resendLoading ? (
              <>
                <FaSpinner className="animate-spin" />
                Verifying...
              </>
            ) : (
              "SUBMIT"
            )}
          </button>
        </form>

        {/* Back to Login Button */}
        <button
          type="button"
          onClick={handleEditPhone}
          className="w-full flex items-center justify-center gap-2 py-2 text-blue-600 hover:text-blue-700 transition"
        >
          <FaArrowLeft />
          Change Phone Number
        </button>

        {/* Footer Text */}
        <p className="footer-disclaimer text-xs text-gray-500 text-center mt-4">
          I agree to receive critical messages such as OTP,
          booking details via SMS.
        </p>
      </div>
    </div>
  );
}
